/**
 * #89 · Confirmación de las tres acciones sobre una etiqueta.
 *
 * Las tres son irreversibles: los estados a los que llevan no tienen salidas
 * en `services/shared/states.py`, así que no hay "deshacer" y no puede
 * haberlo — poder volver de VOIDED sería poder blanquear inventario.
 *
 * Por eso son dos pasos, con el mismo patrón que el retiro de un lote y la
 * denuncia de un rollo robado:
 *
 *   1. qué le pasa a la etiqueta, y el motivo (obligatorio);
 *   2. tipear el código impreso para confirmar.
 *
 * El código tipeado NO es teatro del lado del cliente: viaja como
 * `confirm_code` y la API lo compara contra el de la ruta. Un `curl` con el
 * código equivocado falla igual. El paso 2 existe para que la mano se detenga
 * un segundo; la garantía está en el servidor.
 *
 * El diálogo se monta sólo mientras la acción está en curso y se desmonta al
 * cerrar: desmontar tira el estado del formulario y evita que el segundo paso
 * quede pre-confirmado la próxima vez.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { labelUnitKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { ErrorState } from '@/ui/states';
import { Dialog } from '../products/ui/Dialog';
import { DetailList, DetailRow, TextAreaField, TextField } from '../products/ui/fields';
import {
  ACTION_EFFECT_KEYS,
  ACTION_LABEL_KEYS,
  MIN_REASON,
  type LabelActionName,
} from './units';

type LabelActionOut = Schemas['LabelActionOut'];

type Step = 'reason' | 'confirm' | 'done';

export interface LabelActionDialogProps {
  code: string;
  action: LabelActionName;
  /** Estado actual, para mostrar de dónde sale. */
  fromStateLabel: string;
  onClose: () => void;
}

export function LabelActionDialog({
  code,
  action,
  fromStateLabel,
  onClose,
}: LabelActionDialogProps): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('reason');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [typedCode, setTypedCode] = useState('');
  const [touched, setTouched] = useState(false);

  const apply = useMutation<LabelActionOut, unknown>({
    mutationFn: () =>
      api.post<LabelActionOut>(`/api/v1/labels/${encodeURIComponent(code)}/${action}`, {
        reason: reason.trim(),
        reference: reference.trim() === '' ? null : reference.trim(),
        confirm_code: typedCode.trim().toUpperCase(),
      }),
    onSuccess: () => {
      setStep('done');
      void queryClient.invalidateQueries({ queryKey: labelUnitKeys.all });
    },
  });

  const reasonError =
    touched && reason.trim().length < MIN_REASON ? t('labels.units.reason.tooShort') : undefined;
  const codeMatches = typedCode.trim().toUpperCase() === code;

  return (
    <Dialog
      open
      onClose={onClose}
      tone={step === 'done' ? 'neutral' : 'critical'}
      title={
        step === 'done'
          ? t('labels.units.action.done.title')
          : t(ACTION_LABEL_KEYS[action])
      }
      description={
        step === 'done'
          ? undefined
          : step === 'reason'
            ? t('labels.units.action.step1')
            : t('labels.units.action.step2')
      }
      footer={
        step === 'done' ? (
          <Button variant="primary" onClick={onClose}>
            {t('labels.units.action.done.close')}
          </Button>
        ) : step === 'reason' ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('form.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setTouched(true);
                if (reason.trim().length >= MIN_REASON) setStep('confirm');
              }}
            >
              {t('batches.recall.continue')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setStep('reason');
              }}
              disabled={apply.isPending}
            >
              {t('batches.recall.back')}
            </Button>
            <Button
              variant="critical"
              disabled={!codeMatches || apply.isPending}
              onClick={() => {
                apply.mutate();
              }}
            >
              {apply.isPending
                ? t('labels.units.action.applying')
                : t('labels.units.action.confirm')}
            </Button>
          </>
        )
      }
    >
      {step === 'reason' && (
        <div className="space-y-4">
          <div className="rounded-(--radius-card) border border-border bg-surface-sunken p-4">
            <DetailList>
              <DetailRow
                label={t('labels.units.column.code')}
                value={<code className="font-mono text-xs font-semibold">{code}</code>}
              />
              <DetailRow label={t('labels.units.detail.from')} value={fromStateLabel} />
            </DetailList>
          </div>

          <ul className="list-disc space-y-1 pl-5 text-sm text-ink">
            <li>{t(ACTION_EFFECT_KEYS[action])}</li>
            <li>{t('labels.units.action.effect.audit')}</li>
            <li className="font-medium text-critical">
              {t('labels.units.action.effect.irreversible')}
            </li>
          </ul>

          <TextAreaField
            label={t('labels.units.reason')}
            hint={t('labels.units.reason.hint')}
            value={reason}
            onChange={setReason}
            error={reasonError}
            required
            rows={3}
            maxLength={500}
          />

          <TextField
            label={t('labels.units.reference')}
            hint={t('labels.units.reference.hint')}
            value={reference}
            onChange={setReference}
            maxLength={120}
            mono
          />
        </div>
      )}

      {step === 'confirm' && (
        <div className="space-y-4">
          <p className="text-sm text-ink">{t('labels.units.action.confirm.summary', { code })}</p>
          <TextField
            label={t('labels.units.action.typeCode')}
            hint={t('batches.recall.typeLot.hint')}
            value={typedCode}
            onChange={setTypedCode}
            error={
              typedCode.length > 0 && !codeMatches
                ? t('batches.recall.typeLot.mismatch')
                : undefined
            }
            required
            mono
            disabled={apply.isPending}
          />
          {Boolean(apply.error) && <ErrorState error={apply.error} />}
        </div>
      )}

      {step === 'done' && apply.data !== undefined && (
        <div className="space-y-3">
          <p className="text-sm text-ink">
            {t('labels.units.action.done.detail', {
              code,
              state: apply.data.to_status_name,
            })}
          </p>
          {/*
            El número de entrada de auditoría se muestra porque es lo que se
            le cita al auditor, y porque un investigador que abre un caso lo
            pega en la ficha del caso. Sin él, encontrar esta acción dentro
            de la cadena exige buscar por fecha.
          */}
          <p className="text-xs text-ink-subtle">
            {t('labels.units.action.done.audit', {
              seq: fmt.number(apply.data.audit_seq),
            })}
          </p>
          {!apply.data.cache_invalidated && (
            <p className="text-xs font-medium text-suspect">
              {t('labels.units.action.done.cacheWarning')}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
